import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { Modal, Button, Input, Textarea, RobbieMark } from '../ui';
import { LoginError } from './LoginControls';
import authClient from '../../client/authClient';

/**
 * "Talk to our team" enquiry modal — opened from the login page while logged
 * OUT; posts to the public /auth/enquiries endpoint through the shared
 * client (no auth token involved). Composes the shared ui primitives; the
 * hidden `website` input is the server's honeypot — humans never see it,
 * bots that fill it get a polite 201 and a SPAM-shelved doc. All four
 * visible fields are required, and the success state stays open until the
 * user dismisses it — no toast, no auto-close (owner decisions, 28 Jul 2026).
 */
interface EnquiryModalProps {
  open: boolean;
  onClose: () => void;
}

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type FieldErrors = Partial<Record<'name' | 'email' | 'company' | 'message', string>>;

const errorLine = (error: any): string => {
  if (error?.response?.status === 429) {
    return "We've received a lot from this connection — try again in a bit.";
  }
  if (error?.response?.status === 400) {
    return "I couldn't send that — check your details and try again.";
  }
  return "I couldn't reach the team just now — give it another try in a moment.";
};

const EnquiryModal: React.FC<EnquiryModalProps> = ({ open, onClose }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState(''); // honeypot — stays empty for humans
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      setName('');
      setEmail('');
      setCompany('');
      setMessage('');
      setWebsite('');
      setPending(false);
      setSent(false);
      setFieldErrors({});
      setError(null);
    }
  }, [open]);

  const validate = (): FieldErrors => {
    const errors: FieldErrors = {};
    if (!name.trim()) errors.name = 'I need your name to pass along.';
    if (!email.trim()) {
      errors.email = 'I need a work email the team can reply to.';
    } else if (!EMAIL_SHAPE.test(email.trim())) {
      errors.email = "That email doesn't look complete — check it once more.";
    }
    if (!company.trim()) errors.company = "Tell me which company you're with.";
    if (!message.trim()) errors.message = "Give me a line on what you're sourcing.";
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pending || sent) return;
    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setError(null);
    setPending(true);
    try {
      await authClient.submitEnquiry({
        name: name.trim(),
        email: email.trim(),
        company: company.trim(),
        message: message.trim(),
        website: website || undefined,
      });
      setSent(true);
    } catch (err: any) {
      setError(errorLine(err));
    } finally {
      setPending(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      initialFocusRef={nameRef}
      title={
        <span className="flex items-center gap-s3">
          <RobbieMark size="sm" state={sent ? 'done' : 'waving'} aria-label="Robbie" />
          Talk to our team
        </span>
      }
    >
      {sent ? (
        // Persistent thanks state — stays until the user dismisses it
        // (owner: give people time to read; no toast, no auto-close).
        <div className="flex flex-col items-center gap-s3 py-s5 text-center">
          <p className="text-row-title text-ink">Got it — your note is with the team.</p>
          <p className="text-body-sm text-ink-secondary">
            They'll reach out at your email to get you set up.
          </p>
          <Button className="mt-s3" onClick={onClose}>
            Done
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate>
          <p className="mb-s5 text-body-sm text-ink-secondary">
            I'll pass your note straight to the team — leave your details and they'll reach out to
            get you set up.
          </p>

          {error && <LoginError message={error} />}

          <div className="flex flex-col gap-s4">
            <Input
              ref={nameRef}
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your full name"
              autoComplete="name"
              required
              disabled={pending}
              error={fieldErrors.name}
            />
            <Input
              label="Work email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@company.com"
              autoComplete="email"
              required
              disabled={pending}
              error={fieldErrors.email}
            />
            <Input
              label="Company"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Where you build"
              autoComplete="organization"
              required
              disabled={pending}
              error={fieldErrors.company}
            />
            <Textarea
              label="What are you sourcing?"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="A line or two is plenty"
              maxLength={2000}
              rows={3}
              required
              disabled={pending}
              error={fieldErrors.message}
            />
          </div>

          {/* Honeypot — off-screen, skipped by keyboard and screen readers. */}
          <input
            type="text"
            name="website"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className="absolute left-[-9999px] top-auto h-px w-px overflow-hidden"
          />

          <div className="mt-s6 flex justify-end gap-s2">
            <Button variant="secondary" onClick={onClose} disabled={pending}>
              Cancel
            </Button>
            {/* Same right-arrow glyph as the login card's Continue button —
                one gesture across the login surface (owner). */}
            <Button type="submit" disabled={pending} iconRight={<ArrowRight />}>
              {pending ? 'Sending…' : 'Send to the team'}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default EnquiryModal;
